import { Big } from 'big.js';
import { DisplayData, TenantJobLine } from '~/lib/model';
import { FIRST_VERSION_TIMESTAMP, ZodVersionedMetadata } from '~/lib/zod';
import BreJob from '~/tenants/bre/model/BreJob';
import { BreOrderType } from '~/tenants/bre/model/enums';
import BreBoostConfig from '~/tenants/bre/performable/boost/BreBoostConfig';
import { BreBoostRate } from '~/tenants/bre/performable/boost/BreBoostData';
import { BreProviderId } from '~/tenants/bre/model/BreProvider';
import BreWebsiteConfig from '~/tenants/bre/performable/website/BreWebsiteConfig';
import { datetime } from '~/lib/datettime';

export default class BreBoostJob extends BreJob<typeof BreBoostConfig> {
  get performable(): typeof BreBoostConfig {
    return BreBoostConfig;
  }

  get eligible(): boolean {
    return this.isOneOf(BreOrderType.RESIDENTIAL, BreOrderType.COMMERCIAL, BreOrderType.PROPERTY_MKTG);
  }

  get assignee(): string | null {
    return BreProviderId.PRINT_TEAM.toString();
  }

  get configurable(): boolean {
    return true;
  }

  get dependencies(): string[] {
    return [BreWebsiteConfig.id];
  }

  info(): DisplayData[] {
    const info = super.info();

    info.push({
      name: 'Reach',
      value: this.metadata.reach,
      provider: true,
    });

    info.push({
      name: 'Start At',
      value: datetime(this.metadata.start_at, this.order.timezone).toFormat('LLL dd, yyyy'),
      provider: true,
    });

    return info;
  }

  defaultValue(): ZodVersionedMetadata<(typeof BreBoostConfig)['schema']> {
    return {
      version: FIRST_VERSION_TIMESTAMP,
      reach: '3,000',
      start_at: this.orderCreated.toSQLDate(),
    };
  }

  expenseLines(): TenantJobLine[] {
    const lines = super.expenseLines();
    const { reach } = this.metadata;

    if (reach) {
      lines.push({
        id: 'reach',
        description: `${reach} Potential Reach`,
        amount: new Big(BreBoostRate[reach].expense),
      });
    }

    return lines;
  }

  revenueLines(): TenantJobLine[] {
    const lines = super.revenueLines();
    const { reach } = this.metadata;

    if (reach) {
      lines.push({
        id: 'reach',
        description: `${reach} Potential Reach`,
        amount: new Big(BreBoostRate[reach].revenue),
        discountable: this.orderContainsSocialPackage,
      });
    }

    return lines;
  }
}
